import { memo, useCallback, useMemo } from 'react';
import { ManualArea } from '@thive/fddm-types';

import { SelectInput } from '@common/components/inputs';
import { useReadSelectedAreas } from '@/modules/floorplanDesign/hooks';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { isManualArea } from '@/modules/floorplanDesign/helpers/typeguards';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';

const MANUAL_LOAD_HANDLING_TYPE_OPTIONS: {
  label: string;
  value: Required<ManualArea['loadHandlingType']>;
}[] = [
  { label: 'Intake', value: 'INTAKE' },
  { label: 'Delivery', value: 'DELIVERY' },
  { label: 'Intake and delivery', value: 'INTAKE_AND_DELIVERY' },
];

const getSelectedManualArea = (
  selectedAreas: ReturnType<typeof useReadSelectedAreas>,
): ManualArea | null => {
  const areas = areasToFlatCollection(selectedAreas);

  if (areas.length !== 1) return null;

  const area = areas[0];

  if (!isManualArea(area)) return null;

  return area;
};

function ManualLoadHandlingTypeComponent() {
  const selectedAreas = useReadSelectedAreas();

  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const area = useMemo(() => getSelectedManualArea(selectedAreas), [selectedAreas]);

  const onChange = useCallback(
    (newValue: ManualArea['loadHandlingType']) => {
      if (!area) return;
      fddmSvc.setPreference('manualArea.loadHandlingType', newValue);
      fddmSvc.updateArea({
        ...area,
        loadHandlingType: newValue,
      });
    },
    [area, fddmSvc],
  );

  if (!area) return null;

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_handling_type'>
      <SelectInput
        options={MANUAL_LOAD_HANDLING_TYPE_OPTIONS}
        onChange={onChange}
        value={area.loadHandlingType}
      />
    </PropertiesLabel>
  );
}

export const LoadHandlingType = memo(ManualLoadHandlingTypeComponent);
